import { useAuth } from 'hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { routes } from 'constants/routes';
import RewardsLogo from 'assets/RewardsLogo.svg';
import { ReactComponent as BusinessLogo } from 'assets/BusinessLogo.svg';
import PaymentManagementIcon from 'assets/icons/PaymentManagementIcon';
import HamburgerIcon from 'assets/icons/HamburgerIcon';
import { GoSignOut } from 'react-icons/go';
import { useState } from 'react';
import useIsMobile from 'hooks/useIsMobile';
import Render from 'components/Common/Render';
import { NavigationButton } from 'components/Buttons';

const LeftNavigation = () => {
  const authContext = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', path: routes.home, comingSoon: false },
    { label: 'List your business', path: routes.listBusiness, comingSoon: false },
    { label: 'Instant Pay', path: routes.instantPay, comingSoon: false },
    { label: 'Subscriptions', path: routes.subscriptionTiers, comingSoon: false },
    { label: 'Offers', path: routes.offers, comingSoon: true },
    { label: 'Double Up', path: routes.doubleUp, comingSoon: true },
  ];

  const isChooseBusinessPage = location.pathname.includes('/choose-business');

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    authContext?.logout();
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Header with Hamburger */}
        <div className="fixed top-0 left-0 right-0 h-[60px] bg-white border-b border-borderGrey flex items-center justify-between px-4 z-50">
          <img src={RewardsLogo} alt="Open Rewards" className="h-6" />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-darkBlue hover:text-primary transition-colors"
            aria-label="Toggle menu"
          >
            <HamburgerIcon isOpen={isMobileMenuOpen} />
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="fixed top-[60px] left-0 right-0 bottom-0 bg-white z-50 overflow-y-auto">
              <div className="flex flex-col h-full">
                {/* Navigation Items - Hidden on Choose Business page */}
                <Render when={!isChooseBusinessPage}>
                  <div className="flex-1 py-4">
                    {navigationItems.map((item) => (
                      <NavigationButton
                        key={item.path}
                        label={item.label}
                        onClick={() => handleNavigation(item.path)}
                        isActive={isActivePath(item.path)}
                        isMobile={true}
                        className="mb-1"
                        icon={
                          item.comingSoon ? (
                            <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-grayLight text-grayDark">
                              Coming Soon
                            </span>
                          ) : undefined
                        }
                        iconPosition="right"
                      />
                    ))}
                  </div>
                </Render>

                {/* Action Buttons - Hidden on Choose Business page */}
                <Render when={!isChooseBusinessPage}>
                  <div className="px-4 py-6 border-t border-borderGrey">
                    <NavigationButton
                      label="Switch Business"
                      onClick={() => handleNavigation(`${routes.chooseBusiness}/1`)}
                      icon={<BusinessLogo />}
                      isMobile={true}
                      className="mb-2"
                    />

                    <NavigationButton
                      label="Payment Management"
                      onClick={() => handleNavigation(routes.paymentManagement)}
                      icon={<PaymentManagementIcon />}
                      isMobile={true}
                      className="mb-2"
                    />

                    <NavigationButton
                      label="Log out"
                      onClick={handleLogout}
                      icon={<GoSignOut />}
                      variant="danger"
                      isMobile={true}
                    />
                  </div>
                </Render>

                {/* User Info Section */}
                <Render when={!!authContext?.user}>
                  <div className="px-4 py-6 border-t border-borderGrey">
                    {isChooseBusinessPage ? (
                      <>
                        {/* On Choose Business page: Logout first, then User */}
                        <NavigationButton
                          label="Log out"
                          onClick={handleLogout}
                          icon={<GoSignOut />}
                          variant="danger"
                          isMobile={true}
                        />
                        <div className="-mx-4 w-[calc(100%+2rem)] h-px bg-borderGrey my-4" />
                        <div className="flex items-center gap-3 px-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center font-poppins font-semibold text-base text-white bg-indigo">
                            {authContext?.user?.name?.charAt(0).toUpperCase() || 'S'}
                          </div>
                          <span className="text-darkBlue font-poppins font-medium text-base leading-[112%]">
                            {authContext?.user?.name || 'User'}
                          </span>
                        </div>
                      </>
                    ) : (
                      /* On other pages: User only */
                      <div className="flex items-center gap-3 px-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-poppins font-semibold text-base text-white bg-indigo">
                          {authContext?.user?.name?.charAt(0).toUpperCase() || 'S'}
                        </div>
                        <span className="text-darkBlue font-poppins font-medium text-base leading-[112%]">
                          {authContext?.user?.name || 'User'}
                        </span>
                      </div>
                    )}
                  </div>
                </Render>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div className="w-[250px] h-screen flex flex-col justify-between bg-white border-r border-borderGrey">
      {/* Top Section - Logo */}
      <div className="pt-8 pb-6 px-6">
        <img src={RewardsLogo} alt="Open Rewards" className="h-8" />
      </div>

      {/* Middle Section - Navigation Items - Hidden on Choose Business page */}
      <Render when={!isChooseBusinessPage}>
        <div className="flex-1 py-2">
          {navigationItems.map((item) => (
            <NavigationButton
              key={item.path}
              label={item.label}
              onClick={() => handleNavigation(item.path)}
              isActive={isActivePath(item.path)}
              className="mb-1 leading-none pr-4 pl-6"
              icon={
                item.comingSoon ? (
                  <span className="text-[9px] font-semibold px-2 py-1 rounded-full bg-grayLight text-grayDark whitespace-nowrap">
                    Coming Soon
                  </span>
                ) : undefined
              }
              iconPosition="right"
            />
          ))}
        </div>
      </Render>

      {/* Bottom Section - Actions & User */}
      <div>
        {/* Action Buttons - Hidden on Choose Business page */}
        <Render when={!isChooseBusinessPage}>
          <div className="px-4 py-6 border-t border-borderGrey">
            <NavigationButton
              label="Switch Business"
              onClick={() => handleNavigation(`${routes.chooseBusiness}/1`)}
              icon={<BusinessLogo />}
              className="mb-2"
            />

            <NavigationButton
              label="Payment Management"
              onClick={() => handleNavigation(routes.paymentManagement)}
              icon={<PaymentManagementIcon />}
              className="mb-2"
            />

            <NavigationButton
              label="Log out"
              onClick={handleLogout}
              icon={<GoSignOut />}
              variant="danger"
            />
          </div>
        </Render>

        {/* User Info Section */}
        <Render when={!!authContext?.user}>
          <div className="px-4 py-6 border-t border-borderGrey">
            {isChooseBusinessPage ? (
              <>
                {/* On Choose Business page: Logout first, then User */}
                <NavigationButton
                  label="Log out"
                  onClick={handleLogout}
                  icon={<GoSignOut />}
                  variant="danger"
                />
                <div className="-mx-4 w-[calc(100%+2rem)] h-px bg-borderGrey my-4" />
                <div className="flex items-center gap-3 px-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center font-poppins font-semibold text-base text-white bg-indigo">
                    {authContext?.user?.name?.charAt(0).toUpperCase() || 'S'}
                  </div>
                  <span className="text-darkBlue font-poppins font-medium text-sm leading-[112%]">
                    {authContext?.user?.name || 'User'}
                  </span>
                </div>
              </>
            ) : (
              /* On other pages: User only */
              <div className="flex items-center gap-3 px-2">
                <div className="w-5 h-5 rounded-full flex items-center justify-center font-poppins font-semibold text-base text-white bg-indigo">
                  {authContext?.user?.name?.charAt(0).toUpperCase() || 'S'}
                </div>
                <span className="text-darkBlue font-poppins font-medium text-sm leading-[112%]">
                  {authContext?.user?.name || 'User'}
                </span>
              </div>
            )}
          </div>
        </Render>
      </div>
    </div>
  );
};

export default LeftNavigation;
